import { Check, CheckCircle2, LockKeyhole, ShieldCheck, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export function AiDataList({ tone, icon: Icon, title, items }) {
  const styles =
    tone === 'safe'
      ? 'border-emerald-200 bg-emerald-50/60 text-emerald-950 dark:border-emerald-500/30 dark:bg-emerald-500/5 dark:text-foreground'
      : 'border-destructive/30 bg-destructive/5 text-foreground'
  const headStyles = tone === 'safe' ? 'text-emerald-800 dark:text-emerald-400' : 'text-destructive'
  const markStyles = tone === 'safe' ? 'text-emerald-700 dark:text-emerald-400' : 'text-destructive'

  return (
    <div className={['rounded-md border p-3', styles].join(' ')}>
      <div className={['mb-2 flex items-center gap-1.5 text-xs font-semibold', headStyles].join(' ')}>
        <Icon className="h-4 w-4 shrink-0" />
        {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-1.5 text-xs leading-5 text-muted-foreground">
            {tone === 'safe' ? (
              <Check className={['mt-0.5 h-3.5 w-3.5 shrink-0', markStyles].join(' ')} />
            ) : (
              <X className={['mt-0.5 h-3.5 w-3.5 shrink-0', markStyles].join(' ')} />
            )}
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// One-time "your data will leave the browser" gate shown before an AI feature
// first runs – the same promise Julio's consent screen makes, generalized so
// every AI feature (plan-doc extraction, estimator assist, …) states exactly
// what it sends before it sends anything. Julio keeps its own richer dialog.
//
// The optional secondary action is for features with a local-only fallback
// ("scan locally instead"): declining AI should degrade, not dead-end.
export default function AiConsentDialog({
  open,
  onOpenChange,
  title,
  description,
  intro,
  maySend,
  neverSend,
  acceptLabel = 'Enable and continue',
  onAccept,
  secondaryLabel = null,
  onSecondary,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <p className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2.5 text-xs leading-5 text-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>{intro}</span>
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <AiDataList tone="safe" icon={CheckCircle2} title="Sent to the AI" items={maySend} />
            <AiDataList tone="never" icon={LockKeyhole} title="Never sent" items={neverSend} />
          </div>

          <p className="text-xs leading-5 text-muted-foreground">
            Data is sent over an encrypted connection under a Zero Data Retention policy – nothing is stored or used
            for training. Your choice is remembered on this device only.
          </p>
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-1">
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          {secondaryLabel && (
            <Button variant="outline" size="sm" onClick={onSecondary}>{secondaryLabel}</Button>
          )}
          <Button size="sm" onClick={onAccept}>{acceptLabel}</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
